// Landing page on `/`. Two modes:
//  • DEFAULT — resolve the ONE env program (`Constants.PROGRAM_ID`) via
//    `program.get`; apiv2 does the access check server-side, so success →
//    redirect, WsError 214 → "ask an admin", 380 → "no care team found".
//  • `?switch=1` — the full team picker (all enrolled teams). URL-only today;
//    kept so multi-program switching needs no code change when we serve >1.

import { useEffect, type ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { Icon, WiconsName, IconsLibrary } from '@platform/witems';
import { useEnrolledPrograms, useProgram, type ProgramData } from '@/state/data/programs';
import { useUserCohorts, type CohortData } from '@/state/data/cohorts';
import { Constants } from '@/config/constants';

// apiv2 `program.get` outcomes, surfaced as `WsError.status` (see the service).
const WRONGPERM = 214; // program exists, caller lacks rights (cohort enrolment)
const OBJECT_NOTFOUND = 380; // program doesn't exist / inactive

// ── Shared UI bits ──────────────────────────────────────────────────────────
function FullSpinner() {
  return (
    <div className="loading-center" style={{ minHeight: '60vh' }}>
      <div className="loading-spinner" />
    </div>
  );
}

function GateMessage({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div style={{ maxWidth: 560, margin: '64px auto', padding: 24 }}>
      <h2 className="header3">{title}</h2>
      <p className="body2" style={{ marginTop: 12, color: 'var(--wi-text-secondary)' }}>
        {children}
      </p>
    </div>
  );
}

// ── Default: resolve the env program, redirect or explain ──────────────────
function CareTeamGate() {
  const navigate = useNavigate();
  const pid = String(Constants.PROGRAM_ID);
  const { data: program, isLoading, error } = useProgram(pid);
  // Warm the cohort cache (the env cohort lives here); session-static, harmless
  // if unused downstream, and shared with the ?switch picker's fetch.
  useUserCohorts();

  useEffect(() => {
    if (program) navigate(`/team/${pid}`, { replace: true });
  }, [program, pid, navigate]);

  // While loading, or after a successful fetch (mid-redirect), show the spinner
  // so the gate never flashes an error/empty state before navigating.
  if (isLoading || program) return <FullSpinner />;

  const status = (error as { status?: number } | null)?.status;
  if (status === WRONGPERM) {
    return (
      <GateMessage title="Access needed">
        You don&apos;t have access to this care team yet. Ask an administrator to
        add you, then reload this page.
      </GateMessage>
    );
  }
  if (status === OBJECT_NOTFOUND) {
    return (
      <GateMessage title="No care team found">
        We couldn&apos;t find the care team for this environment
        {' '}
        (
        <code>{pid}</code>
        ). If this is unexpected, contact the platform team.
      </GateMessage>
    );
  }
  return (
    <GateMessage title="Couldn’t load your care team">
      {(error as Error | null)?.message ?? 'An unexpected error occurred. Try reloading.'}
    </GateMessage>
  );
}

// ── ?switch=1: pick among ALL enrolled care teams ──────────────────────────
interface CohortGroup {
  cohortid: string | number;
  cohortName: string;
  programs: ProgramData[];
}

function groupByCohort(programs: ProgramData[], cohorts: CohortData[]): CohortGroup[] {
  const byCohortId = new Map<string, CohortGroup>();
  programs.forEach((p) => {
    const key = String(p.cohortid ?? 'unknown');
    let group = byCohortId.get(key);
    if (!group) {
      const cohort = cohorts.find((c) => String(c.cohortid) === key);
      group = { cohortid: p.cohortid ?? 'unknown', cohortName: cohort?.name ?? `Cohort ${key}`, programs: [] };
      byCohortId.set(key, group);
    }
    group.programs.push(p);
  });
  return Array.from(byCohortId.values());
}

function TeamPicker() {
  const navigate = useNavigate();
  const programsQuery = useEnrolledPrograms();
  const cohortsQuery = useUserCohorts();

  const programs = programsQuery.data;
  const cohorts = cohortsQuery.data;
  const isLoading = programsQuery.isLoading || cohortsQuery.isLoading;
  const error = programsQuery.error ?? cohortsQuery.error ?? null;

  if (isLoading) return <FullSpinner />;
  if (error) {
    return <GateMessage title="Couldn’t load care teams">{error.message}</GateMessage>;
  }
  if (!programs || programs.length === 0) {
    return (
      <GateMessage title="No care teams">
        You&apos;re not enrolled in any care team. Contact the platform team to be added.
      </GateMessage>
    );
  }

  const groups = groupByCohort(programs, cohorts ?? []);

  return (
    <div style={{ maxWidth: 880, margin: '64px auto', padding: 24 }}>
      <header style={{ textAlign: 'center', marginBottom: 32 }}>
        <h1 className="header3">Choose a care team</h1>
        <p className="body2" style={{ marginTop: 8, color: 'var(--wi-text-secondary)' }}>
          Pick the care team you want to work on.
        </p>
      </header>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        {groups.map((group) => (
          <section
            key={String(group.cohortid)}
            style={{
              padding: 20,
              borderRadius: 16,
              border: '1px solid var(--wi-divider)',
              background: 'var(--wi-background-secondary)',
            }}
          >
            <header style={{ marginBottom: 16 }}>
              <h2 className="header3" style={{ margin: 0 }}>{group.cohortName}</h2>
            </header>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
              {group.programs.map((p) => (
                <div
                  key={String(p.programid)}
                  role="button"
                  tabIndex={0}
                  className="care-team-card"
                  aria-label={`Open ${p.name ?? `program ${p.programid}`}`}
                  onClick={() => navigate(`/team/${p.programid}`)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      navigate(`/team/${p.programid}`);
                    }
                  }}
                >
                  <div className="care-team-card-name header4">
                    {p.name ?? `Program ${p.programid}`}
                  </div>
                  {/* Decorative arrow — the whole card IS the button; a nested
                      witems <Button> would double-fire navigate + nest a button. */}
                  <Icon
                    className="care-team-open-btn"
                    name={WiconsName.NAVIGATION_NEXTARROW}
                    library={IconsLibrary.WICONS}
                    aria-hidden
                  />
                </div>
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}

export default function ChooseCareTeamPage() {
  const [searchParams] = useSearchParams();
  return searchParams.has('switch') ? <TeamPicker /> : <CareTeamGate />;
}
